import { useSessaoStore, type UsuarioAutenticado } from "./store";

/*
 * Selectors de permissão da projeção (FE-2 CP-A — ADR 0026 §RBAC discovery).
 *
 * O `/api/auth/eu` devolve `permissoes: PermissaoUsuario[]`, cada uma com
 * `{ acao, irrestrito, empresas }`. A ação da projeção é a string EXATA que o
 * StrEnum do backend serializa: `"visualizar_projecao"`.
 *
 * `irrestrito === true` significa que o usuário enxerga TODAS as empresas (e o
 * grupo consolidado) — é a condição para ligar a projeção do grupo no CP-C. Sem
 * irrestrição, o usuário só vê as empresas listadas em `empresas` (escopo
 * limitado), e a visão de grupo não se aplica.
 *
 * A função pura é o núcleo testável; o hook é só um wrapper de conveniência sobre
 * o store para a UI consumir reativamente.
 */

/** Ação RBAC da projeção — string EXATA do StrEnum do backend. */
const ACAO_VISUALIZAR_PROJECAO = "visualizar_projecao";

/**
 * O usuário tem projeção IRRESTRITA (vê todas as empresas e o grupo)?
 *
 * `true` somente se houver uma permissão com `acao === "visualizar_projecao"` E
 * `irrestrito === true`. Usuário `null` (não carregado/deslogado) → `false`.
 * Ausência da ação → `false`. Ação presente mas restrita → `false`.
 */
export function temProjecaoIrrestrita(
  usuario: UsuarioAutenticado | null,
): boolean {
  if (usuario === null) {
    return false;
  }
  return usuario.permissoes.some(
    (permissao) =>
      permissao.acao === ACAO_VISUALIZAR_PROJECAO && permissao.irrestrito,
  );
}

/**
 * Hook reativo: o usuário atual do store tem projeção irrestrita?
 *
 * Reexecuta quando o `usuario` do store muda (login/logout/bootstrap). Wrapper
 * fino sobre `temProjecaoIrrestrita` — a lógica testável vive na função pura.
 */
export function useTemProjecaoIrrestrita(): boolean {
  return useSessaoStore((estado) => temProjecaoIrrestrita(estado.usuario));
}

/*
 * Selectors genéricos de permissão (FE-4 CP-A — módulo de Cadastros).
 *
 * O módulo de Cadastros usa ações variadas (`cadastrar_infraestrutura`,
 * `gerenciar_usuarios`, ...) e precisa de um teste de permissão por AÇÃO ARBITRÁRIA
 * — não amarrado à projeção. Estes selectors recebem a string da ação (EXATA do
 * StrEnum do backend) e respondem se o usuário a possui. A lógica pura é o núcleo
 * testável; os hooks são wrappers reativos sobre o store.
 */

/**
 * O usuário possui a `acao` em ALGUM escopo (irrestrito OU lista de empresas
 * não-vazia)?
 *
 * `true` se houver uma permissão com `acao === acao` que conceda acesso a pelo
 * menos uma empresa — seja por `irrestrito === true`, seja por `empresas` não
 * vazio. Uma permissão presente mas SEM escopo algum (irrestrito false e empresas
 * vazio) não concede nada → `false`. Usuário `null` → `false`. Ação ausente →
 * `false`.
 */
export function temPermissao(
  usuario: UsuarioAutenticado | null,
  acao: string,
): boolean {
  if (usuario === null) {
    return false;
  }
  return usuario.permissoes.some(
    (permissao) =>
      permissao.acao === acao &&
      (permissao.irrestrito || permissao.empresas.length > 0),
  );
}

/**
 * O usuário possui a `acao` de forma IRRESTRITA (vê todas as empresas)?
 *
 * `true` somente se houver uma permissão com `acao === acao` E
 * `irrestrito === true`. Usuário `null` → `false`. Ação ausente → `false`. Ação
 * presente mas restrita (apenas `empresas`) → `false`.
 */
export function temPermissaoIrrestrita(
  usuario: UsuarioAutenticado | null,
  acao: string,
): boolean {
  if (usuario === null) {
    return false;
  }
  return usuario.permissoes.some(
    (permissao) => permissao.acao === acao && permissao.irrestrito,
  );
}

/**
 * Hook reativo: o usuário atual do store possui `acao` em algum escopo?
 *
 * Reexecuta quando o `usuario` do store muda. Wrapper fino sobre `temPermissao`.
 */
export function useTemPermissao(acao: string): boolean {
  return useSessaoStore((estado) => temPermissao(estado.usuario, acao));
}

/**
 * Hook reativo: o usuário atual do store possui `acao` de forma irrestrita?
 *
 * Reexecuta quando o `usuario` do store muda. Wrapper fino sobre
 * `temPermissaoIrrestrita`.
 */
export function useTemPermissaoIrrestrita(acao: string): boolean {
  return useSessaoStore((estado) =>
    temPermissaoIrrestrita(estado.usuario, acao),
  );
}
